import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SafeNameInput } from '../ui/safe-name-input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { UserCircle } from 'lucide-react';

export const GuestProfileDialog = ({ open, onOpenChange, profile, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    numVisits: 0,
    preferences: '',
    allergies: '',
    favouriteDrink: '',
    favouriteFood: '',
    likesAndDislikes: '',
    birthday: '',
    anniversary: '',
    additionalComments: ''
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        numVisits: profile.numVisits || 0,
        preferences: profile.preferences || '',
        allergies: profile.allergies || '',
        favouriteDrink: profile.favouriteDrink || '',
        favouriteFood: profile.favouriteFood || '',
        likesAndDislikes: profile.likesAndDislikes || '',
        birthday: profile.birthday || '',
        anniversary: profile.anniversary || '',
        additionalComments: profile.additionalComments || ''
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        numVisits: 0,
        preferences: '',
        allergies: '',
        favouriteDrink: '',
        favouriteFood: '',
        likesAndDislikes: '',
        birthday: '',
        anniversary: '',
        additionalComments: ''
      });
    }
  }, [profile, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <UserCircle className="w-5 h-5 text-teal-600" />
            <span>{profile ? 'Edit Guest Profile' : 'Add Guest Profile'}</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <SafeNameInput
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="Enter first name"
                required
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <SafeNameInput
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder="Enter last name"
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Enter email address"
              required
            />
            <p className="text-xs text-slate-500 mt-1">Email must be unique for each guest profile</p>
          </div>

          {/* Visits (Read-only display) */}
          <div>
            <Label htmlFor="numVisits">Number of Visits</Label>
            <Input
              id="numVisits"
              type="number"
              value={formData.numVisits}
              placeholder="0"
              min="0"
              disabled
              className="bg-slate-100 cursor-not-allowed"
            />
            <p className="text-xs text-slate-500 mt-1">Auto-filled by system based on allocations</p>
          </div>

          {/* Preferences */}
          <div>
            <Label htmlFor="preferences">Preferences</Label>
            <Textarea
              id="preferences"
              value={formData.preferences}
              onChange={(e) => handleChange('preferences', e.target.value)}
              placeholder="Enter guest preferences..."
              rows={2}
            />
          </div>

          {/* Allergies */}
          <div>
            <Label htmlFor="allergies">Allergies</Label>
            <Textarea
              id="allergies"
              value={formData.allergies}
              onChange={(e) => handleChange('allergies', e.target.value)}
              placeholder="Enter any allergies..."
              rows={2}
            />
          </div>

          {/* Favourites */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="favouriteDrink">Favourite Drink</Label>
              <Input
                id="favouriteDrink"
                value={formData.favouriteDrink}
                onChange={(e) => handleChange('favouriteDrink', e.target.value)}
                placeholder="Enter favourite drink"
              />
            </div>
            <div>
              <Label htmlFor="favouriteFood">Favourite Food</Label>
              <SafeNameInput
                id="favouriteFood"
                value={formData.favouriteFood}
                onChange={(e) => handleChange('favouriteFood', e.target.value)}
                placeholder="Enter favourite food"
              />
            </div>
          </div>

          {/* Likes and Dislikes */}
          <div>
            <Label htmlFor="likesAndDislikes">Likes and Dislikes</Label>
            <Textarea
              id="likesAndDislikes"
              value={formData.likesAndDislikes}
              onChange={(e) => handleChange('likesAndDislikes', e.target.value)}
              placeholder="Enter likes and dislikes..."
              rows={2}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="birthday">Birthday</Label>
              <Input
                id="birthday"
                type="date"
                value={formData.birthday}
                onChange={(e) => handleChange('birthday', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="anniversary">Anniversary</Label>
              <Input
                id="anniversary"
                type="date"
                value={formData.anniversary}
                onChange={(e) => handleChange('anniversary', e.target.value)}
              />
            </div>
          </div>

          {/* Additional Comments */}
          <div>
            <Label htmlFor="additionalComments">Additional Comments</Label>
            <Textarea
              id="additionalComments"
              value={formData.additionalComments}
              onChange={(e) => handleChange('additionalComments', e.target.value)}
              placeholder="Any additional comments..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {profile ? 'Update Profile' : 'Create Profile'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
